import * as THREE from "three";
import type { LootPile } from "../inventory/LootPile.js";
import { floorUrl, shouldMirrorFloor } from "../sprites/ItemSpriteResolver.js";
import { INTERACTIVE_LAYER } from "./Raycaster.js";

/**
 * A05c — floor-drop billboard renderer.
 *
 * Replaces the procedural gold-square marker with a billboarded sprite of
 * the pile's "headline" item using {@link floorUrl}. Per-instance mirror
 * flip is hashed from the pile id so the orientation is stable across
 * redraws and save round-trips; items with `floor.mirror = false` in
 * their `item.json` opt out (labeled scrolls, banners, one-sided weapons).
 *
 * When `floorUrl` returns null for the headline item the renderer falls
 * back to the original gold marker so unauthored items still show
 * something the player can click.
 *
 * The mesh carries `userData.kind = "floor_drop"` so the
 * `InteractionDispatcher` routes clicks to the loot modal regardless of
 * whether art was found.
 */

const FLOOR_Y = 0.02; // just above floor — matches the legacy marker
const SPRITE_SIZE = 0.5;

interface PileMesh {
  readonly mesh: THREE.Sprite | THREE.Mesh;
  readonly geometries: THREE.BufferGeometry[];
  readonly materials: THREE.Material[];
  readonly textures: THREE.Texture[];
}

export class LootPileRenderer {
  private readonly scene: THREE.Scene;
  private readonly entries = new Map<string, PileMesh>();
  private readonly imageCache = new Map<string, HTMLImageElement>();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Spawn a marker for `pile`. Async because the headline item's PNG is
   * probed off the network; the procedural fallback shows immediately
   * while the probe is in flight, and gets swapped for the billboard
   * once the image lands.
   */
  spawn(pile: LootPile): void {
    if (this.entries.has(pile.id)) return;

    // Show the procedural marker right away so the cell is clickable
    // even before the texture probe resolves.
    this.spawnProceduralMarker(pile);

    const headline = pile.stacks[0];
    if (!headline) return;
    const itemId = headline.item.id;

    void Promise.all([floorUrl(itemId), shouldMirrorFloor(itemId)]).then(([url, allowMirror]) => {
      if (!url) return;
      if (!this.entries.has(pile.id)) return; // despawned mid-probe
      const mirror = allowMirror && shouldMirrorPile(pile.id);
      this.loadImage(url)
        .then((img) => {
          if (!this.entries.has(pile.id)) return;
          this.replaceWithBillboard(pile, img, mirror);
        })
        .catch(() => {
          // image fetch failed — keep the procedural fallback
        });
    });
  }

  /** Despawn the marker and dispose its GPU resources. */
  despawn(pileId: string): void {
    const entry = this.entries.get(pileId);
    if (!entry) return;
    this.scene.remove(entry.mesh);
    for (const g of entry.geometries) g.dispose();
    for (const m of entry.materials) m.dispose();
    for (const t of entry.textures) t.dispose();
    this.entries.delete(pileId);
  }

  /** Drop every marker — called on map teardown. */
  dispose(): void {
    for (const id of [...this.entries.keys()]) this.despawn(id);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private spawnProceduralMarker(pile: LootPile): void {
    const geo = new THREE.PlaneGeometry(SPRITE_SIZE, SPRITE_SIZE);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffd700, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(pile.position.x, FLOOR_Y, pile.position.z);
    mesh.userData = { instanceId: pile.id, kind: "floor_drop" };
    mesh.layers.enable(INTERACTIVE_LAYER);
    this.scene.add(mesh);

    this.entries.set(pile.id, {
      mesh,
      geometries: [geo],
      materials: [mat],
      textures: [],
    });
  }

  private replaceWithBillboard(pile: LootPile, img: HTMLImageElement, mirror: boolean): void {
    // Tear down the procedural fallback first.
    this.despawn(pile.id);

    const texture = new THREE.CanvasTexture(canvasFromImage(img, mirror));
    // Authored floor.png art is sRGB — tag it or it renders washed out.
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(SPRITE_SIZE, SPRITE_SIZE, 1);
    sprite.position.set(pile.position.x, SPRITE_SIZE / 2 + FLOOR_Y, pile.position.z);
    sprite.userData = { instanceId: pile.id, kind: "floor_drop" };
    sprite.layers.enable(INTERACTIVE_LAYER);
    this.scene.add(sprite);

    this.entries.set(pile.id, {
      mesh: sprite,
      geometries: [],
      materials: [material],
      textures: [texture],
    });
  }

  private async loadImage(url: string): Promise<HTMLImageElement> {
    const cached = this.imageCache.get(url);
    if (cached) return cached;
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(`failed to load ${url}`));
      i.src = url;
    });
    this.imageCache.set(url, img);
    return img;
  }
}

/**
 * Deterministic per-instance mirror flip. Hashing the pile id means the
 * orientation survives save/load round-trips without persisting an extra
 * `mirror` field.
 */
export function shouldMirrorPile(pileId: string): boolean {
  let hash = 0;
  for (let i = 0; i < pileId.length; i++) {
    hash = (hash * 31 + pileId.charCodeAt(i)) | 0;
  }
  return (hash & 1) === 1;
}

function canvasFromImage(img: HTMLImageElement, mirror: boolean): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || 96;
  canvas.height = img.naturalHeight || 96;
  const ctx = canvas.getContext("2d")!;
  if (mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(img, 0, 0);
  return canvas;
}
